import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { StaticFields } from "@graylog/server-api";
import { Button, Table, ButtonToolbar } from "components/bootstrap";
import { ConfirmDialog, Icon, IfPermitted } from "components/common";
import UserNotification from "util/UserNotification";
import { StaticFieldForm } from "components/inputs";
import SectionGrid from "components/common/Section/SectionGrid";
import { KEY_PREFIX } from "hooks/usePaginatedInputs";
const StyledWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacings.md};
`;
const StyledTable = styled(Table)`
  margin-top: ${(props) => props.theme.spacings.md};
`;
const StyledTd = styled.td`
  width: ${(props) => props.theme.spacings.md};
`;
const StyledSpan = styled.span`
  display: flex;
  justify-content: flex-end;
`;
const InputStaticFields = ({ input }) => {
  const { t } = useTranslation("inputs");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showStaticFieldForm, setShowStaticFieldForm] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const queryClient = useQueryClient();
  const handleDeleteStaticField = () => {
    StaticFields.remove(fieldToDelete, input.id).then(
      () => {
        UserNotification.success(t("static_fields.remove_success", { field: fieldToDelete, input: input.title }));
        setFieldToDelete(null);
        setShowConfirmDelete(false);
        queryClient.invalidateQueries({ queryKey: KEY_PREFIX });
      },
      (error) => {
        UserNotification.error(
          t("static_fields.remove_failed", { error }),
          t("static_fields.remove_failed_title", { field: fieldToDelete, input: input.title })
        );
      }
    );
  };
  const onDeleteStaticField = (fieldName) => {
    setFieldToDelete(fieldName);
    setShowConfirmDelete(true);
  };
  const deleteButton = (fieldName) => /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "transparent",
      bsSize: "xsmall",
      style: { verticalAlign: "baseline" },
      onClick: () => onDeleteStaticField(fieldName)
    },
    /* @__PURE__ */ React.createElement(Icon, { name: "close" })
  );
  const formatStaticFields = (staticFields) => {
    const formattedFields = [];
    const staticFieldNames2 = Object.keys(staticFields);
    staticFieldNames2.forEach((fieldName) => {
      formattedFields.push(
        /* @__PURE__ */ React.createElement("tr", { key: `${fieldName}-field` }, /* @__PURE__ */ React.createElement("td", null, fieldName), /* @__PURE__ */ React.createElement("td", null, staticFields[fieldName]), /* @__PURE__ */ React.createElement(StyledTd, null, /* @__PURE__ */ React.createElement(ButtonToolbar, null, deleteButton(fieldName))))
      );
    });
    return formattedFields;
  };
  const staticFieldNames = Object.keys(input.static_fields);
  if (staticFieldNames.length === 0) {
    return /* @__PURE__ */ React.createElement("div", null);
  }
  return /* @__PURE__ */ React.createElement(StyledWrapper, null, /* @__PURE__ */ React.createElement(SectionGrid, null, /* @__PURE__ */ React.createElement("h4", null, t("static_fields.title")), /* @__PURE__ */ React.createElement(StyledSpan, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:edit:${input.id}`, `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      bsSize: "xs",
      key: `add-static-field-${input.id}`,
      onClick: () => {
        setShowStaticFieldForm(true);
      }
    },
    t("static_fields.add")
  )))), /* @__PURE__ */ React.createElement(StyledTable, { condensed: true }, /* @__PURE__ */ React.createElement("tbody", null, formatStaticFields(input.static_fields))), showConfirmDelete && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: t("static_fields.delete_title"),
      show: true,
      onConfirm: () => handleDeleteStaticField(),
      onCancel: () => setShowConfirmDelete(false)
    },
    t("static_fields.delete_confirm", { field: fieldToDelete, input: input.title })
  ), showStaticFieldForm && /* @__PURE__ */ React.createElement(StaticFieldForm, { input, setShowModal: setShowStaticFieldForm }));
};
export default InputStaticFields;
